import { useAppConfig } from 'application/configuration'
import { useListSmartContractAPI } from 'features/smart-contract/list/useListSmartContractAPI'
import { usePriceFormatter } from 'lib/use-price-formatter'
import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import { LabeledIcon } from 'ui/labeled-icon'
import { Tooltip } from 'ui/tooltip'

import { getProjectName } from '../../../lib/get-project-name'
import { SkeletonBlock } from '../../skeleton-block'

export type Props = {
  lower: null | number
  higher: null | number
  smartContractEnabled?: boolean
  disclaimer?: PriceDisclaimerProps
  showRange?: boolean
  alwaysForceRange?: boolean
  skeleton?: boolean
  crossPrice?: boolean
}

export const EmptyPrice: FC = ({ children }) => (
  <PricesWrapper>
    <LowerPrice>{children}</LowerPrice>
  </PricesWrapper>
)

export const Price: FC<Props> = ({
  lower,
  skeleton = false,
  higher,
  disclaimer,
  showRange = false,
  smartContractEnabled = false,
  alwaysForceRange = false,
  crossPrice = true
}) => {
  const formatPrice = usePriceFormatter()

  const higherPrice = higher === null ? null : formatPrice(higher)

  const lowerPrice = lower === null ? null : formatPrice(lower > 0 ? lower : 0)

  const disclaimerText = useMemo(() => {
    if (showRange && disclaimer?.content.range) return disclaimer.content.range
    if (!showRange && disclaimer?.content.final) return disclaimer.content.final
    return null
  }, [showRange, disclaimer])

  const { state } = useListSmartContractAPI()

  const {
    i18n: {
      common: { from: fromText, to: toText }
    }
  } = useAppConfig()

  /* Если цены не переданы ничего не выыводим*/
  if (higherPrice === null && lowerPrice === null) return null

  if (skeleton)
    return (
      <PricesWrapper>
        <LowerPrice>
          <SkeletonBlock width={'150px'} height={'3.3rem'} />
        </LowerPrice>
      </PricesWrapper>
    )

  /* Если цены равны минимальная цена*/
  if (higherPrice === lowerPrice && state?.tradeIn.behaviour !== 'enabled_without_calculations')
    return (
      <PricesWrapper>
        <LowerPrice>
          {showRange && alwaysForceRange && <PriceRange>{fromText}</PriceRange>} {lowerPrice}
          {!smartContractEnabled && disclaimer && disclaimer.content.range && (
            <Disclaimer content={disclaimer.content.range} />
          )}
        </LowerPrice>
      </PricesWrapper>
    )

  return (
    <PricesWrapper>
      {lowerPrice && (
        <LowerPrice>
          {showRange === true && <PriceRange>{fromText}</PriceRange>} {lowerPrice}
        </LowerPrice>
      )}

      {!lowerPrice && <LowerPrice> {formatPrice(0)} </LowerPrice>}

      {higherPrice !== lowerPrice && (
        <PriceWithDisclaimer>
          <HigherPrice crossed={crossPrice} grayedOut={true}>
            {showRange && toText} {higherPrice}
          </HigherPrice>
          {disclaimer && disclaimerText && <Disclaimer content={disclaimerText} />}
        </PriceWithDisclaimer>
      )}
    </PricesWrapper>
  )
}

export const CreditPrice: FC<{ value: number; showRange: boolean }> = ({ value, showRange = false }) => {
  const formatPrice = usePriceFormatter()

  const {
    i18n: {
      common: { from: fromText, to: toText, monthForms }
    }
  } = useAppConfig()

  return (
    <PricesWrapper>
      <LowerPrice>
        {showRange && <PriceRange>{fromText}</PriceRange>} {formatPrice(value)}
        <PeriodText>/{monthForms[0]}</PeriodText>
      </LowerPrice>
    </PricesWrapper>
  )
}

export const PriceWithDisclaimer = styled.div`
  display: flex;
  align-items: center;
`

export type PriceDisclaimerProps = {
  content: {
    final?: string
    range?: string
  }
  text?: string
}

const Disclaimer: FC<{ content: string; text?: string }> = ({ content, text }) => {
  return (
    <>
      <Info data-for="priceDisclaimer" data-tip={content}>
        {getProjectName() !== 'avito' && (
          <LabeledIcon icon={'info'} size={getProjectName() === 'avito' ? 'normal' : 'small'} type={'primary'}>
            {text}
          </LabeledIcon>
        )}
      </Info>
      <Tooltip id="priceDisclaimer" />
    </>
  )
}

const PeriodText = styled.div`
  color: ${(props) => props.theme.palette.grey[500]};
  ${({ theme }) => theme.textStyles.h6}
`

const PricesWrapper = styled.div`
  display: flex;
  flex-direction: ${() => getProjectName() === 'avito' && 'column'};
  flex-wrap: wrap;
  align-items: ${() => (getProjectName() === 'avito' ? 'left' : 'center')};
  margin-bottom: ${() => (getProjectName() === 'avito' ? 0 : '1rem')};
`

const PriceRange = styled.div`
  ${({ theme }) => theme.textStyles.h5};
  display: inline;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  margin-right: 0.5rem;
`

const LowerPrice = styled.div`
  display: inline-flex;
  align-items: ${() => (getProjectName() === 'avito' ? 'center' : 'baseline')};
  margin-right: 0.8rem;
  ${({ theme }) =>
    getProjectName() === 'avito'
      ? 'font-size: 2rem; font-family: "Manrope",-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto" ,"Helvetica Neue", Arial, sans-serif,  "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"'
      : theme.textStyles.h4};
  font-weight: ${() => (getProjectName() === 'avito' ? 700 : 400)};
  color: ${({ theme }) => {
    if (getProjectName() === 'generic_v2') return '#D9D9D9'
    if (getProjectName() === 'tank') return theme.palette.white
    return theme.palette.black
  }};
  font-size: ${() => {
    return getProjectName() === 'haval' ? '1.4rem' : null
  }};
`

const HigherPrice = styled.div<{ crossed?: boolean; grayedOut?: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  font-weight: 300;
  ${({ theme }) => theme.textStyles.h6}
  color: ${({ theme, grayedOut = false }) => (grayedOut ? theme.palette.grey[500] : theme.palette.grey[900])};
  text-decoration: ${({ crossed }) => (crossed ? 'line-through' : 'none')};
`

const Info = styled.div`
  margin-left: ${() => (getProjectName() === 'avito' ? '5px' : '1rem')};
  margin-top: ${() => (getProjectName() === 'avito' ? '3px' : '1rem')};
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;

  color: ${({ theme }) => theme.palette.brand.primary};
  ${({ theme }) => theme.textStyles.p3}
  cursor: pointer;
`

const TextFinalPrice = styled.div`
  width: 100%;
  font-size: 14px;
`
